/* global YT, Vimeo */

if (!customElements.get('video-component')) {
  class Video extends HTMLElement {
    constructor() {
      super();
      this.autoplay = this.dataset.autoplay === 'true';
      this.background = this.dataset.background === 'true';
      this.naturalWidth = this.dataset.naturalWidth === 'true';
      this.pausedByUser = false;
      this.inViewport = false;
    }

    connectedCallback() {
      this.init();
    }

    init() {
      const url = this.dataset.videoUrl;

      if (url) {
        if (url.includes('youtu.be') || url.includes('youtube.com')) {
          this.videoId = url.includes('v=') ? url.split('v=').pop().split('&')[0] : url.split('?')[0].split('/').pop();
          this.type = 'youtube';
          this.initYouTube();
        } else if (url.includes('vimeo.com')) {
          this.videoId = url.split('?')[0].split('/').pop();
          this.type = 'vimeo';
          this.initVimeo();
        }
      } else {
        this.type = 'tag';
        this.initVideoTag();
      }

      if (this.background) {
        this.closest('.video-section').addEventListener('click', this.play.bind(this));
      }
    }

    loadScript(src) {
      if (document.querySelector(`script[src="${src}"]`)) return;

      const s = document.createElement('script');
      s.src = src;
      s.onerror = (err) => console.warn(`Unable to load script ${src}`, err);

      if (this.type === 'vimeo') {
        s.onload = () => document.dispatchEvent(new CustomEvent('on:vimeo-api:loaded'));
      } else if (this.type === 'youtube') {
        window.onYouTubeIframeAPIReady = () => document.dispatchEvent(new CustomEvent('on:youtube-api:loaded'));
      }

      document.body.appendChild(s);
    }

    initYouTube() {
      const initYTPlayer = () => {
        YT.ready(() => {
          this.player = new YT.Player(this.querySelector('div'), {
            videoId: this.videoId,
            width: '1280',
            height: '720',
            playerVars: {
              controls: this.background ? 0 : 1,
              disablekb: this.background ? 1 : 0,
              iv_load_policy: 3,
              modestbranding: 1,
              playsinline: 1,
              rel: 0
            },
            events: {
              onReady: this.handleYTReady.bind(this),
              onStateChange: this.handleYTStateChange.bind(this)
            }
          });
        });
      };

      if (!window.YT) {
        document.addEventListener('on:youtube-api:loaded', initYTPlayer.bind(this));
        this.loadScript('//www.youtube.com/iframe_api');
      } else {
        initYTPlayer();
      }
    }

    initVimeo() {
      const initVimeoPlayer = async () => {
        try {
          this.player = new Vimeo.Player(this, {
            id: this.videoId,
            width: '1280',
            height: '720',
            autoplay: this.autoplay,
            background: this.background,
            keyboard: !this.background,
            muted: this.autoplay
          });

          await this.player.ready();
          this.setupIframe('js-vimeo');

          this.player.on('pause', () => {
            if (this.inViewport) this.pausedByUser = true;
          });

          this.player.on('play', () => {
            this.closest('.video-section').classList.add('video-section--played');
          });

          this.play();
        } catch (error) {
          console.error('Error initializing Vimeo player:', error);
        }
      };

      if (!window.Vimeo) {
        document.addEventListener('on:vimeo-api:loaded', initVimeoPlayer.bind(this));
        this.loadScript('//player.vimeo.com/api/player.js');
      } else {
        initVimeoPlayer();
      }
    }

    initVideoTag() {
      this.player = this.querySelector('video');

      this.player.addEventListener('pause', () => {
        if (this.inViewport) this.pausedByUser = true;
      });

      if (this.player.currentTime > 0 && !this.player.paused && !this.player.ended && this.player.readyState > 2) {
        this.closest('.video-section').classList.add('video-section--played');
      } else {
        this.player.addEventListener('play', () => {
          this.closest('.video-section').classList.add('video-section--played');
        });

        this.addObserver(this.player);

        const deferredMedia = this.closest('deferred-media');
        if (!deferredMedia || deferredMedia.loadTrigger !== 'click') {
          this.player.muted = true;
        }

        this.play();
      }
    }

    handleYTReady() {
      this.setupIframe('js-youtube');
      if (this.autoplay) this.player.mute();
      this.play();
    }

    handleYTStateChange(e) {
      if (e.data === YT.PlayerState.PAUSED) {
        if (this.inViewport) this.pausedByUser = true;
      } else if (e.data === YT.PlayerState.ENDED) {
        if (this.background) this.player.playVideo();
      } else if (e.data === YT.PlayerState.PLAYING) {
        this.closest('.video-section').classList.add('video-section--played');
      }
    }

    setupIframe(jsClass) {
      if (this.autoplay && !this.naturalWidth) {
        this.style.width = `${this.clientHeight * 1.7778}px`;
        window.addEventListener('resize', window.debounce(() => {
          this.style.width = `${this.clientHeight * 1.7778}px`;
        }, 200));
      }

      this.iframe = this.querySelector('iframe');
      this.iframe.title = this.dataset.description;
      this.iframe.classList.add(jsClass);
      this.addObserver(this.iframe);
    }

    play() {
      if (!this.player || this.pausedByUser) return;

      try {
        if (this.type === 'youtube') {
          this.player.playVideo();
        } else {
          const playPromise = this.player.play();
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              if (error.name !== 'AbortError') {
                console.error('Error playing video:', error);
              }
            });
          }
        }
      } catch (error) {
        console.error('Error in play method:', error);
      }
    }

    pause() {
      if (!this.player) return;

      try {
        if (this.type === 'youtube') {
          if (this.player.getPlayerState() !== 2) {
            this.player.pauseVideo();
            this.pausedByUser = false;
          }
        } else if (this.type === 'vimeo') {
          this.player.getPaused().then((paused) => {
            if (!paused) {
              this.player.pause().catch(error => console.error('Error pausing Vimeo video:', error));
              this.pausedByUser = false;
            }
          }).catch(error => console.error('Error checking Vimeo pause state:', error));
        } else if (!this.player.paused) {
          this.player.pause();
          this.pausedByUser = false;
        }
      } catch (error) {
        console.error('Error in pause method:', error);
      }
    }

    addObserver(el) {
      if ('IntersectionObserver' in window === false) return;

      let debounceTimeout;
      const debounce = (func, delay) => {
        clearTimeout(debounceTimeout);
        debounceTimeout = setTimeout(func, delay);
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.inViewport = true;
            debounce(() => this.play(), 100);
          } else {
            this.inViewport = false;
            debounce(() => this.pause(), 100);
          }
        });
      }, { rootMargin: '0px 0px 200px 0px' });

      observer.observe(el);
    }
  }

  customElements.define('video-component', Video);
}